import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const MOBILE_WIDTH = 768;

interface IsMobileContextData {
  breakpointMap: Record<number, boolean>;
  registerBreakpoint: (breakpoint: number) => void;
  unregisterBreakpoint: (breakpoint: number) => void;
}

export const IsMobileContext = createContext<IsMobileContextData | undefined>(
  undefined,
);

interface IsMobileProviderProps {
  children: ReactNode;
}

export const IsMobileProvider = ({ children }: IsMobileProviderProps) => {
  // Keep track of the isMobile state for each breakpoint that we are interested in
  const [breakpointMap, setBreakpointMap] = useState<Record<number, boolean>>({
    [MOBILE_WIDTH]: false,
  });
  // Keep track of the number of times each breakpoint is being monitored. This is because some components
  // can request a different breakpoint size
  const breakpointCountRef = useRef(
    new Map<number, number>([[MOBILE_WIDTH, 1]]),
  );

  // Allow consumers of the context to register their interest in a custom breakpoint size
  const registerBreakpoint = useCallback((breakpoint: number) => {
    const count = breakpointCountRef.current.get(breakpoint) ?? 0;
    breakpointCountRef.current.set(breakpoint, count + 1);

    setBreakpointMap((prev) => {
      const newMap = { ...prev };
      if (!(breakpoint in newMap)) {
        newMap[breakpoint] = window.innerWidth < breakpoint;
      }
      return newMap;
    });
  }, []);

  // Also allow consumers of the context to deregister their interest in a custom breakpoint size
  const unregisterBreakpoint = useCallback((breakpoint: number) => {
    const count = (breakpointCountRef.current.get(breakpoint) ?? 1) - 1;
    if (count <= 0) {
      breakpointCountRef.current.delete(breakpoint);
    } else {
      breakpointCountRef.current.set(breakpoint, count);
    }
  }, []);

  // Add a resize handler in the provider, so that consumers of the context do not have to have their own resize handlers
  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;

      setBreakpointMap((prevMap) => {
        const newMap: Record<number, boolean> = {};
        let hasChanged = false;

        breakpointCountRef.current.forEach((_count, breakpoint) => {
          const newValue = currentWidth < breakpoint;
          newMap[breakpoint] = newValue;
          if (newValue !== prevMap[breakpoint]) {
            hasChanged = true;
          }
        });

        // Only return the newMap if any values have changed, as this prevents unnecessary re-renders when the
        // breakpoints that we are interested in have not changed
        return hasChanged ? newMap : prevMap;
      });
    };

    // Handle initial size, and register event handler for register
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <IsMobileContext.Provider
      value={{ breakpointMap, registerBreakpoint, unregisterBreakpoint }}
    >
      {children}
    </IsMobileContext.Provider>
  );
};
